import { LuShieldCheck, LuClipboardCheck, LuCheck, LuMessageSquare } from "react-icons/lu";
import { business, differentiators, serviceAreas } from "../../content/site";

/**
 * Icons for each differentiator, keyed by the title in site.js.
 * Presentation only — all copy comes from site.js.
 */
const cardIcons = {
  "Licensed and insured": LuShieldCheck,
  "Upfront pricing": LuClipboardCheck,
  "Clean, code-compliant work": LuCheck,
  "Straight answers": LuMessageSquare,
};

/**
 * VERIFICATION GATE
 * -----------------------------------------------------------------------
 * Two entries in `differentiators` carry `requiresVerification: true`:
 *
 *   "Licensed and insured" — a legal claim. It may only render once the
 *     owner supplies the Texas electrical contractor license number, so it
 *     is gated on `business.licenseNumber` being present. While that field
 *     is `null`, the card does not exist.
 *
 *   "Upfront pricing" — a business-practice claim (price approved in
 *     writing before work starts). There is no corresponding confirmed
 *     field in site.js yet, so it stays `false`. Flip it to a real check
 *     the moment the owner confirms the policy and a field exists to hang
 *     it on. Do not hardcode `true`.
 *
 * Anything not listed here is unverified by default.
 */
const verifiedClaims = {
  "Licensed and insured": Boolean(business.licenseNumber),
  "Upfront pricing": false,
};

const visibleDifferentiators = differentiators.filter(
  (item) => !item.requiresVerification || verifiedClaims[item.title] === true
);

const WhyUs = () => (
  <section id="why-us" className="section bg-surface">
    <div className="container-page">
      <p className="kicker">Why homeowners call us back</p>
      <h2 className="mt-3 max-w-prose text-heading-lg text-ink">
        A trade service you do not have to supervise.
      </h2>

      <div className="mt-10 grid gap-6 lg:grid-cols-3">
        {visibleDifferentiators.map((item, index) => {
          const Icon = cardIcons[item.title];
          /* The first card leads: wider on large screens, heavier type,
             brand-tinted surface. The rest stay quiet so the eye lands
             somewhere instead of scanning four identical tiles. */
          const isLead = index === 0;

          return (
            <article
              key={item.title}
              className={
                isLead
                  ? "card min-w-0 bg-surface-brand-soft border-line-brand p-6 sm:p-8 lg:col-span-2"
                  : "card min-w-0 p-6"
              }
            >
              {Icon ? (
                <Icon
                  aria-hidden="true"
                  className={
                    isLead
                      ? "h-8 w-8 text-ink-brand"
                      : "h-6 w-6 text-ink-brand"
                  }
                />
              ) : null}
              <h3
                className={
                  isLead
                    ? "mt-4 text-heading-lg text-ink [overflow-wrap:anywhere]"
                    : "mt-4 text-heading text-ink [overflow-wrap:anywhere]"
                }
              >
                {item.title}
              </h3>
              <p
                className={
                  isLead
                    ? "mt-3 max-w-prose text-body-lg text-ink-muted"
                    : "mt-2 max-w-prose text-body text-ink-muted"
                }
              >
                {item.body}
              </p>
            </article>
          );
        })}

        {/* The local-knowledge block. Prose, not a card grid item, so the
            section does not read as a wall of equal tiles. */}
        <div className="min-w-0 rounded-lg border border-line bg-surface-sunken p-6 sm:p-8 lg:col-span-3">
          <h3 className="text-heading text-ink">
            What working in {serviceAreas.primaryMetro} actually takes
          </h3>
          <p className="mt-3 max-w-prose text-body text-ink-muted">
            North Texas housing stock runs from mid-century slab homes still
            carrying two-prong outlets to new builds that hit panel capacity the
            day an EV shows up in the driveway. On top of that, permitting and
            inspection are handled city by city across the metroplex, and the
            requirements are not identical.
          </p>
          <p className="mt-3 max-w-prose text-body text-ink-muted">
            Knowing which situation you are in before the truck pulls up is the
            difference between one visit and three. That judgment is the part of
            this trade you cannot get from a national dispatch center, and it is
            what {business.shortName} brings to a service call.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default WhyUs;
